import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";

import { getClientes } from "../helpers/rutaClientes";
import { getProveedores } from "../helpers/rutaProveedores";
import {
  postAjusteCtaCte,
  putAjusteCtaCte,
} from "../helpers/rutaAjustesCtaCte";


const hoyISO = () => new Date().toISOString().slice(0, 10);

const norm = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .trim();

const nombreEntidad = (x) =>
  x?.razonsocial || x?.cliente || x?.proveedor || x?.nombre || "";

const parseMoney = (value) => {
  const raw = String(value ?? "")
    .replace(/[^\d.,-]/g, "")
    .trim();

  if (!raw) return 0;

  if (raw.includes(",")) {
    return Number(raw.replace(/\./g, "").replace(",", "."));
  }

  const dotCount = (raw.match(/\./g) || []).length;

  if (dotCount > 0) {
    const parts = raw.split(".");
    const last = parts[parts.length - 1];

    if (dotCount > 1 || last.length === 3) {
      return Number(raw.replace(/\./g, ""));
    }

    return Number(raw);
  }

  return Number(raw);
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ModalAjusteCtaCte({
  show,
  onHide,
  entidadTipoInicial = "CLIENTE",
  movimientoInicial = "A_COBRAR",
  clienteInicial = null,
  proveedorInicial = null,
  ajuste = null,
}) {
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    fecha: hoyISO(),
    entidadTipo: "CLIENTE",
    cliente: "",
    proveedor: "",
    movimiento: "A_COBRAR",
    concepto: "",
    importeA: "",
    importeB: "",
    importeFlete: "",
    observaciones: "",
  });

  const editando = Boolean(ajuste?._id);

  const entidadBloqueada = Boolean(
    editando || clienteInicial?._id || proveedorInicial?._id
    );

useEffect(() => {
  if (!show) return;

  if (ajuste?._id) {
    const entidadTipo = ajuste.entidadTipo || "CLIENTE";

    setForm({
      fecha: ajuste.fecha ? String(ajuste.fecha).slice(0, 10) : hoyISO(),
      entidadTipo,
      cliente:
        entidadTipo === "CLIENTE"
          ? ajuste?.cliente?._id || ajuste?.cliente || ""
          : "",
      proveedor:
        entidadTipo === "PROVEEDOR"
          ? ajuste?.proveedor?._id || ajuste?.proveedor || ""
          : "",
      movimiento: ajuste.movimiento || "A_COBRAR",
      concepto: ajuste.concepto || "",
      importeA: String(ajuste.importeA || "").replace(".", ","),
      importeB: String(ajuste.importeB || "").replace(".", ","),
      importeFlete: String(ajuste.importeFlete || "").replace(".", ","),
      observaciones: ajuste.observaciones || "",
    });

    setErr("");
    return;
  }

  const entidadTipo =
    proveedorInicial?._id
      ? "PROVEEDOR"
      : clienteInicial?._id
      ? "CLIENTE"
      : entidadTipoInicial;

  setForm({
    fecha: hoyISO(),
    entidadTipo,
    cliente: clienteInicial?._id || "",
    proveedor: proveedorInicial?._id || "",
    movimiento: movimientoInicial || "A_COBRAR",
    concepto: "",
    importeA: "",
    importeB: "",
    importeFlete: "",
    observaciones: "",
  });

  setErr("");
}, [
  show,
  entidadTipoInicial,
  movimientoInicial,
  clienteInicial,
  proveedorInicial,
  ajuste,
]);

  useEffect(() => {
    if (!show) return;

    const cargar = async () => {
      setLoading(true);
      setErr("");

      try {
        const [cli, prov] = await Promise.all([getClientes(), getProveedores()]);
        setClientes(Array.isArray(cli) ? cli : []);
        setProveedores(Array.isArray(prov) ? prov : []);
      } catch (e) {
        console.error("❌ Cargando clientes/proveedores:", e);
        setErr("No se pudieron cargar clientes/proveedores.");
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [show]);

  const listaEntidades = useMemo(() => {
    const arr = form.entidadTipo === "CLIENTE" ? clientes : proveedores;

    return [...(arr || [])].sort((a, b) =>
      nombreEntidad(a).localeCompare(nombreEntidad(b), "es", {
        sensitivity: "base",
      })
    );
  }, [form.entidadTipo, clientes, proveedores]);

  const setF = (k, v) => {
    setForm((prev) => ({
      ...prev,
      [k]: v,
    }));
  };

  const total = useMemo(() => {
    return (
      parseMoney(form.importeA) +
      parseMoney(form.importeB) +
      parseMoney(form.importeFlete)
    );
  }, [form.importeA, form.importeB, form.importeFlete]);

  const guardar = async () => {
    setErr("");

    const entidadId =
      form.entidadTipo === "CLIENTE" ? form.cliente : form.proveedor;

    if (!entidadId) {
      setErr("Seleccioná cliente o proveedor.");
      return;
    }

    if (!String(form.concepto || "").trim()) {
      setErr("Ingresá un concepto.");
      return;
    }

    if (total <= 0) {
      setErr("El importe total debe ser mayor a cero.");
      return;
    }

    const payload = {
      fecha: form.fecha,
      entidadTipo: form.entidadTipo,
      movimiento: form.movimiento,
      concepto: norm(form.concepto),
      observaciones: form.observaciones || "",
      importeA: parseMoney(form.importeA),
      importeB: parseMoney(form.importeB),
      importeFlete: parseMoney(form.importeFlete),
    };

    if (form.entidadTipo === "CLIENTE") {
      payload.cliente = form.cliente;
    } else {
      payload.proveedor = form.proveedor;
    }

    setSaving(true);

    try {
      const resp = editando
        ? await putAjusteCtaCte(ajuste._id, payload)
        : await postAjusteCtaCte(payload);

      if (!resp?.ok) {
        throw new Error(resp?.error || resp?.msg || "No se pudo guardar el ajuste.");
      }

      onHide?.(true);
    } catch (e) {
      console.error("❌ Guardar ajuste:", e);
      setErr(e?.message || "Error guardando ajuste.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
        show={show}
        onHide={() => onHide?.(false)}
        size="lg"
        backdrop="static"
        animation={false}
        restoreFocus={false}
        >
      <Modal.Header closeButton>
        <Modal.Title>
            {editando ? "Editar Ajuste de Cuenta Corriente" : "Nuevo Ajuste de Cuenta Corriente"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {err ? <Alert variant="danger">{err}</Alert> : null}

        <Row className="g-2">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Fecha</Form.Label>
              <Form.Control
                type="date"
                value={form.fecha}
                onChange={(e) => setF("fecha", e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Entidad</Form.Label>
              <Form.Select
                value={form.entidadTipo}
                disabled={entidadBloqueada}
                onChange={(e) => {
                  const entidadTipo = e.target.value;

                  setForm((prev) => ({
                    ...prev,
                    entidadTipo,
                    cliente: "",
                    proveedor: "",
                  }));
                }}
              >
                <option value="CLIENTE">Cliente</option>
                <option value="PROVEEDOR">Proveedor</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>
                {form.entidadTipo === "CLIENTE" ? "Cliente" : "Proveedor"}
              </Form.Label>

              <Form.Select
                disabled={loading || entidadBloqueada}
                value={form.entidadTipo === "CLIENTE" ? form.cliente : form.proveedor}
                onChange={(e) => {
                  if (form.entidadTipo === "CLIENTE") {
                    setF("cliente", e.target.value);
                  } else {
                    setF("proveedor", e.target.value);
                  }
                }}
              >
                <option value="">
                  {loading ? "Cargando..." : "-- Seleccionar --"}
                </option>

                {listaEntidades.map((x) => (
                  <option key={x._id} value={x._id}>
                    {nombreEntidad(x)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Movimiento</Form.Label>
              <Form.Select
                value={form.movimiento}
                onChange={(e) => setF("movimiento", e.target.value)}
              >
                <option value="A_COBRAR">A cobrar</option>
                <option value="A_PAGAR">A pagar</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={8}>
            <Form.Group>
              <Form.Label>Concepto</Form.Label>
              <Form.Control
                value={form.concepto}
                onChange={(e) => setF("concepto", e.target.value)}
                placeholder="Ej: SERVICIO EXTRAORDINARIO / DIFERENCIA / BONIFICACIÓN"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Importe A</Form.Label>
              <Form.Control
                value={form.importeA}
                onChange={(e) => setF("importeA", e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Importe B</Form.Label>
              <Form.Control
                value={form.importeB}
                onChange={(e) => setF("importeB", e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Importe Flete</Form.Label>
              <Form.Control
                value={form.importeFlete}
                onChange={(e) => setF("importeFlete", e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </Form.Group>
          </Col>

          <Col md={12}>
            <div
              className="mt-2 p-2"
              style={{
                background: "#eef7ee",
                border: "1px solid #d5ead5",
                borderRadius: 6,
                fontWeight: 700,
              }}
            >
              Total ajuste: $ {fmtMoney(total)}
            </div>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.observaciones}
                onChange={(e) => setF("observaciones", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide?.(false)} disabled={saving}>
          Cancelar
        </Button>

        <Button variant="primary" onClick={guardar} disabled={saving || loading}>
          {saving ? "Guardando..." : editando ? "Guardar cambios" : "Guardar ajuste"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}